import type { MouseEvent } from 'react'

import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BilateralTradeListItem } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import {
  stickyActionHeaderStyle,
  useStickyRightShadow,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import {
  TABLE_MESSAGE_BOX_STYLE,
  TABLE_MESSAGE_CELL_STYLE,
} from '~/components/common/TableMessageRow'
import { decimalToNumber } from '~/models/decimal'
import { TradeRow } from '~/pages/SupplyBilateral/components/TradeRow'
import { decimalString } from '~/pages/SupplyBilateral/models/formatters'

type Props = {
  items: BilateralTradeListItem[]
  userRole?: string
  /**
   * workflow-back-bar-readonly AC-19: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき削除ボタンを disabled にする（表示切替系ではなく編集・確定操作のため）。
   */
  isReadonly?: boolean
  onRecap: (id: string) => void
  onViewDetail: (id: string) => void
  /**
   * 編集ボタンクリック時のコールバック。
   * TODO(#4801): P0 は編集ボタンを非表示にしたため呼び出し元は渡さない (P1 で復活)。
   */
  onEdit?: (id: string) => void
  onDelete: (id: string, updatedAt: Timestamp | undefined) => void
  onGoTo48: (id: string, event?: MouseEvent) => void
}

/**
 * 取引一覧テーブル (14 列構成、 design/supply/bilateral.html 準拠)。
 *
 * 列構成:
 *   約定経路 / 商品形態 / 約定日 / ステータス / 取引ID / 相対先 / 売買 / エリア /
 *   受渡期間 / 受渡区分 / 時間帯 / 数量(MW) / 単価(円/kWh) / 操作 (ラベル無し)
 *
 * TODO(#4787 / #4801): 「オプション」「実績利用率」「利用率(下限-上限)」の 3 列は
 *   P0 で非表示 (該当データを保持しないため)。 P1 (通告変更管理) で復活させる。
 *
 * 0 件の場合は列見出しを残し、 その直下に空メッセージの行を出す (= AC-006 準拠。
 * UI 辞書 table.md §3 `tr.ecl-empty-state-row`)。
 */
export const TradeTable = ({
  items,
  userRole,
  isReadonly = false,
  onRecap,
  onViewDetail,
  onDelete,
  onGoTo48,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 操作列 sticky の影の出し分け (MasterDataTable と同じ共通 hook / design
  // `ecl-table-sticky-shadow.js` と同条件)。 16 列あるため横スクロールが常態。
  const { wrapRef, stickyShadow } = useStickyRightShadow([items])

  return (
    // design (.ecl-table th, components.css:804-806) は position:sticky; top:0 で
    // thead を縦スクロール内に固定する。 本 table では見送る:
    // 縦スクロールの境界は親 (SupplyBilateralPage) 側の `Box flex={1}
    // overflowY="auto"` が持ち、 この `wrapRef` は横スクロール用に overflowX="auto"
    // だけを指定している。 CSS 仕様上 overflow-x が visible 以外だと overflow-y も
    // 暗黙に auto 化されるため、 この Box 自体が (中身の高さが親に制約されないまま)
    // 独立した縦スクロールコンテナ境界になってしまい、 sticky の基準が親の
    // scrollport ではなくこの Box になる → 親がスクロールしても thead は追従しない
    // (= 実質 sticky が効かない見せかけの固定になる)。 正しく効かせるには
    // MasterDataTable (overflowX/overflowY を同一 Box に持たせ、 その Box 自体に
    // 高さ制約を持たせる構成) と同じへ、 index.tsx の card-body Box まで含めて
    // 縦スクロール所有権を作り直す必要があり、 本タスク (見た目の値合わせ) の
    // scope を超える構造変更のため見送る。
    // `container-type: inline-size` は空メッセージ行の `100cqi` (見えている幅の中央に
    // 置く) のため。 block 軸は含めない (TableMessageRow の JSDoc)。
    <Box ref={wrapRef} overflowX="auto" css={{ containerType: 'inline-size' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {/* [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
                design (`design/supply/bilateral.html`) は 3 列目 (約定日 の後ろ) だが、
                行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
                TradeRow の tbody 側と対で並べ替えている。 */}
            <th style={headerStyleCenter}>
              {t('page:supplyBilateral.table.status')}
            </th>
            <th style={headerStyle}>
              {t('page:supplyBilateral.table.executionRoute')}
            </th>
            <th style={headerStyleCenter}>
              {t('page:supplyBilateral.table.productType')}
            </th>
            <th style={headerStyle}>
              {t('page:supplyBilateral.table.executionDate')}
            </th>
            <th style={headerStylePublicTradeId}>
              {t('page:supplyBilateral.table.publicTradeId')}
            </th>
            <th style={headerStyle}>
              {t('page:supplyBilateral.table.partner')}
            </th>
            <th style={headerStyleCenter}>
              {t('page:supplyBilateral.table.buySell')}
            </th>
            <th style={headerStyleCenter}>
              {t('page:supplyBilateral.table.area')}
            </th>
            <th style={headerStyle}>
              {t('page:supplyBilateral.table.deliveryPeriod')}
            </th>
            <th style={headerStyleCenter}>
              {t('page:supplyBilateral.table.deliveryType')}
            </th>
            <th style={headerStyleCenter}>
              {t('page:supplyBilateral.table.timeZoneType')}
            </th>
            {/* TODO(#4787 / #4801): 「オプション」「実績利用率」「利用率(下限-上限)」の
                3 ヘッダは P0 では該当データを保持しないため非表示 (i18n key
                `table.option` / `table.actualUtilizationRate` / `table.utilizationRange`
                は残置)。 P1 (通告変更管理) で `TradeRow` のセル 3 つと対で戻す。 */}
            <th style={headerStyleRight}>
              {t('page:supplyBilateral.table.volumeMw')}
            </th>
            <th style={headerStyleRight}>
              {t('page:supplyBilateral.table.unitPriceYenPerKwh')}
            </th>
            {/* 操作列: design/CLAUDE.md「操作列は右揃え固定・TH ラベルなし」準拠でラベル無し
                (名前は aria-label で与える)。 design (table-th-align.js) は cell-actions の
                text-align:right を TH にも同期するため、 揃えは right + セル同様 sticky right:0。 */}
            <th
              aria-label={t('common:table.actionsLabel')}
              // 影は globalCss の `::before` が描く (border-collapse 表では
              // td/th 直当ての box-shadow が paint されない。 issue #5489)。
              data-sticky-shadow={stickyShadow}
              style={{
                ...headerStyleActions,
                ...stickyActionHeaderStyle(TABLE_HEADER_BG),
              }}
            />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            // 表の空行 (UI 辞書 table.md §3)。 生 table なので共通の TableMessageRow
            // (DS TableRow / TableCell) は使えず、 その style 定数を引く (値の持ち主は 1 か所)。
            <tr style={{ height: '100%' }}>
              <td colSpan={TABLE_COLUMN_COUNT} style={TABLE_MESSAGE_CELL_STYLE}>
                <div style={TABLE_MESSAGE_BOX_STYLE}>
                  {t('page:supplyBilateral.empty')}
                </div>
              </td>
            </tr>
          )}
          {items.map((item, index) => (
            <TradeRow
              key={item.id}
              actualUtilizationRate={decimalToNumber(
                item.actualUtilizationRate,
              )}
              areaCode={item.areaCode}
              buySell={item.buySell === Side.BUY ? 'buy' : 'sell'}
              customBands={item.customBands}
              deliveryPeriodFrom={item.deliveryPeriodFrom}
              deliveryPeriodTo={item.deliveryPeriodTo}
              deliveryType={item.deliveryType}
              executionDate={item.executionDate}
              executionRoute={item.executionRoute}
              hasOption={item.hasOption}
              id={item.id}
              isManual={item.isManual}
              isReadonly={isReadonly}
              onDelete={(id) => onDelete(id, item.updatedAt)}
              onGoTo48={onGoTo48}
              onRecap={onRecap}
              onViewDetail={onViewDetail}
              partnerCode={item.partnerSubmissionEntityCode}
              partnerName={item.partnerName}
              productType={item.productType}
              publicTradeId={item.publicTradeId}
              rowIndex={index}
              status={item.status}
              stickyShadow={stickyShadow}
              timeZoneType={item.timeZoneType}
              unitPriceYenPerKwh={decimalToNumber(item.unitPriceYenPerKwh)}
              userRole={userRole}
              utilizationRateMax={decimalToNumber(item.utilizationRateMax)}
              utilizationRateMin={decimalToNumber(item.utilizationRateMin)}
              volumeMw={decimalString(item.volumeMw)}
            />
          ))}
        </tbody>
      </table>
    </Box>
  )
}

/** thead の列数 (= 空メッセージ行の colSpan)。 TradeRow のセル数と同じ。 */
const TABLE_COLUMN_COUNT = 14

const headerStyle: React.CSSProperties = {
  textAlign: 'left',
  // design (.ecl-table th) の padding: 10px 12px に揃える (§2.5 実測)。
  padding: '10px 12px',
  // design (.ecl-table th) は明示 font-size を持たず基準サイズ 14px (= md) を継承する
  // (§2.5 実測: design computed font-size = 14px。 以前の sm(12px) 指定は乖離だったため修正)。
  fontSize: fontSizes.md.value,
  fontWeight: 600,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  // design (.ecl-table th, components.css:801) の background: var(--surface-table-header) に揃える
  // (以前は無指定で白のまま乖離していた)。 TABLE_HEADER_BG は他の生 table 実装
  // (MasterDataTable / SpotPrice の Stats・KomaTable 等) が共有する single source。
  background: TABLE_HEADER_BG,
  // design (.ecl-table th, components.css:798) は border-bottom: 1px solid var(--border-low)。
  // 以前の 2px solid Border.midEmphasis は乖離だったため修正。
  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
  whiteSpace: 'nowrap',
}

const headerStyleRight: React.CSSProperties = {
  ...headerStyle,
  textAlign: 'right',
}

/**
 * 「取引ID」列 (デザイナー指摘 2026-08-13: 可読性が悪いので幅を広げたい)。
 *
 * 8080 (`design/supply/bilateral.html`) はこの列に幅指定を持たない。 表示値
 * (`publicTradeId` = 取引ID) の書式桁数から必要幅を算出する:
 * `docs/specs/admin/data/uploads/bilateral-trade.md` #2「20文字以内
 * （guidelines/input-character-limits.md の識別子系推奨値に準拠）」。
 * `20ch` (= 半角文字 20 個ぶん) を最小幅とし、 それ未満の実データでは
 * 詰まらない (table-layout:auto の既定では折り返し可能な区切り文字 `-` が
 * あるため、 幅指定なしだと極端に狭い時に改行されうる)。
 *
 * design/CLAUDE.md「Table」節の規約通り、 th 側 (本定義) と td 側
 * (`TradeRow` の `cellStylePublicTradeId`) の両方に同じ幅を与える
 * (= 片方だけだと auto layout algorithm が広い方を採用してしまう)。
 */
const headerStylePublicTradeId: React.CSSProperties = {
  ...headerStyle,
  minWidth: '20ch',
}

const headerStyleCenter: React.CSSProperties = {
  ...headerStyle,
  textAlign: 'center',
}

/**
 * 操作列ヘッダ。 固定 (sticky) / 影 / 左右 8px padding は共通ロジック
 * (`stickyActionHeaderStyle`) が担当するため、 ここでは揃えと背景だけ持つ。
 * sticky cell は透明だと下を流れる他列が透けるので背景の指定は必須
 * (= 他の TH と同じ TABLE_HEADER_BG。 以前は Surface.primary(白) で他列と背景が
 * 割れていた)。
 */
const headerStyleActions: React.CSSProperties = {
  ...headerStyle,
  textAlign: 'right',
  background: TABLE_HEADER_BG,
}
